import React, { useEffect, useState } from 'react';

import { Card, CardContent } from 'components/ui/card';
import { useTimezone } from '../contexts/TimezoneContext';
import { formatDateInTimezone, formatRelativeTime, isToday } from '../utils/dateUtils';

interface TimeSample {
  serverTime: string;
  localTime: string;
  isToday: boolean;
  relativeTime: string;
}

export const TimezoneTest: React.FC = () => {
  const { timezone, offset } = useTimezone();
  const [samples, setSamples] = useState<TimeSample[]>([]);

  useEffect(() => {
    const now = new Date();
    const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000);
    const lastWeek = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

    const times = [now, yesterday, lastWeek];
    const newSamples = times.map((time) => ({
      serverTime: time.toISOString(),
      localTime: formatDateInTimezone(time, timezone),
      isToday: isToday(time, timezone),
      relativeTime: formatRelativeTime(time),
    }));

    setSamples(newSamples);
  }, [timezone]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-foreground">Timezone Test Component</h1>
        <p className="text-sm text-muted-foreground">Utility view for verifying timezone formatting helpers.</p>
      </div>

      <Card className="border-white/10 bg-white/5">
        <CardContent className="space-y-1 text-sm text-foreground">
          <h2 className="text-lg font-semibold text-foreground">User Timezone Information</h2>
          <p>Timezone: {timezone}</p>
          <p>Offset: {offset} minutes</p>
        </CardContent>
      </Card>

      <Card className="border-white/10 bg-white/5">
        <CardContent className="space-y-4 text-sm text-foreground">
          <h2 className="text-lg font-semibold">Time Samples</h2>
          {samples.map((sample, index) => (
            <div key={index} className="rounded-2xl border border-white/10 bg-white/5 p-4">
              <p className="font-semibold text-foreground">Sample {index + 1}</p>
              <p className="text-muted-foreground">Server Time: {sample.serverTime}</p>
              <p className="text-muted-foreground">Local Time: {sample.localTime}</p>
              <p className="text-muted-foreground">Is Today: {sample.isToday ? 'Yes' : 'No'}</p>
              <p className="text-muted-foreground">Relative Time: {sample.relativeTime}</p>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
};
